/**
 * ContractWizard Component
 * Main orchestrator for the contract create/edit workflow with stepper navigation
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Button,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Stop as TerminateIcon,
  EventRepeat as ExtendIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  AccountBalanceWallet as RedemptionIcon,
} from '@mui/icons-material';
import ExtendContractDialog from '../ExtendContractDialog';
import AdhocCapitalAmendmentDialog from '../AdhocCapitalAmendmentDialog';
import ContractActionDialogs from './steps/summary/ContractActionDialogs';
import SubmitForApprovalDialog from '../SubmitForApprovalDialog';
import { usePermissions } from '../../../contexts/PermissionContext';
import { MODULES, PERMISSION_LEVELS } from '../../../contexts/PermissionConstants';
import useServerPDF from '../../../hooks/useServerPDF';
import { useForm, FormProvider } from 'react-hook-form';
import { format } from 'date-fns';

import { ContractFormProvider, useContractFormContext } from './ContractFormContext';
import { useNotification } from '../../../contexts/NotificationContext';
import WizardStepper, { getSteps } from './WizardStepper';
import WizardNavigation from './WizardNavigation';
import ContractDetailsStep from './steps/ContractDetailsStep';
import InterestPaymentsStep from './steps/InterestPaymentsStep';
import CapitalEventsFeesStep from './steps/CapitalEventsFeesStep';
import FeesSecurityStep from './steps/FeesSecurityStep';
import ReviewScheduleStep from './steps/ReviewScheduleStep';
import ContractSummaryStep from './steps/ContractSummaryStep';
import useContractReferenceData from '../../../hooks/useContractReferenceData';
import useContractValidation from '../../../hooks/useContractValidation';
import { apiPost, apiPut, apiGet, apiDelete, extractApiError } from '../../../utils/apiClient';
import logger from '../../../utils/logger';

// Default form values for new contracts
const getDefaultValues = () => ({
  is_multi_investor: false,
  investor_id: '',
  product_id: '',
  capital_amount: '',
  new_funds_amount: '',
  rolled_funds_amount: 0,
  rollover_sources: [],   // T3-032: many-to-1 rolled-funds source rows (single-investor path)
  user_reference: '',
  investment_currency_id: '',
  settlement_currency_id: '',
  settlement_fx_rate: '',
  capital_received_in_settlement_ccy: false,
  investment_term: 'Years',
  investment_term_value: '',
  deposit_date: format(new Date(), 'yyyy-MM-dd'),
  maturity_date: '',
  funds_received_date: '',
  first_payment_date: '',
  rate_type: 'Fixed',
  day_count_basis: '',
  likelihood_to_roll: '',
  interest_rate: '',
  interest_paid_frequency_id: '',
  capital_paid_frequency_id: '',
  benchmark_rate_id: null,
  rate_spread: 0,
  rate_change_effective_basis: 'immediate',
  // T3-068: specified_date rate-change cadence (frequency + first date). Empty unless the basis
  // is 'On Specified Date'; buildPayload nulls them for any other basis.
  rate_change_frequency_id: '',
  first_rate_change_date: '',
  // T3-098: optional CUSTOM cadence overriding the frequency's months (e.g. every 18 months).
  // Empty = use the selected frequency. The InterestPaymentsStep gate is derived from the unit.
  rate_change_custom_unit: '',
  rate_change_custom_value: '',
  rate_change_mode: 'bulk',
  exclude_leap_day: false,
  eligible_for_repricing: false,
  repricing_frequency_id: '',
  first_repricing_date: '',
  eligible_for_renewal: false,
  renewal_frequency_id: '',
  first_renewal_date: '',
  allow_schedule_editing: false,
  is_brokered: false,
  broker_id: '',
  broker_fees: '',
  // T3-012: broker-fee VAT snapshot (vat % from contract country; amounts derived).
  broker_fee_vat_percent: '',
  broker_fee_exclusive_amount: '',
  broker_fee_vat_amount: '',
  broker_fee_inclusive_amount: '',
  // T3-084: cross-border VAT treatment override. STANDARD = the country rate (current
  // behaviour); the companions are only meaningful for NO_VAT. Note the non-'' defaults —
  // the treatment backs a NOT NULL column and the flag backs a checkbox.
  broker_fee_vat_treatment: 'STANDARD',
  broker_fee_settled_in_other_ccy: false,
  broker_fee_settlement_currency_id: '',
  broker_fee_vat_note: '',
  placement_fees_id: '',
  issuer_id: '',
  program_id: '',
  stamp_duty: false,
  stamp_duty_fees: '',
  stamp_duty_fees_id: '',
  has_collateral: false,
  collateral_provider_id: '',
  collateral_type_id: '',
  collateral_amount: '',
  collateral_multiplier: '',
  collateral_in_favour_of: '',
  collateral_description: '',
  calculation_profile_id: '',
  capitalization_frequency_id: '',
  equal_installment: false,
  combine_payment_rows: false,
});

// An investor's rollover-source breakdown, normalised to the shape the API expects:
// [{source_contract_id, rolled_amount}] with positive amounts only, converted by the same
// settlement→contract factor used for the scalar (invFactor = rate when received-in-settlement-ccy,
// else 1) so the per-source sum equals the investor's rolled_funds_amount in the CONTRACT currency.
// Shared by the investor add/update payload (where it tells the server this is a MULTI-source roll,
// so the whole-total-vs-first-source cap must not fire) and by the rollover-sources PUT (the sole
// junction WRITER) — one derivation, so the two can never disagree about what the sources are.
const sourcesForApi = (investor, invFactor) => {
  const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
  const raw = Array.isArray(investor?.rollover_sources) ? investor.rollover_sources : [];
  return raw
    .filter((s) => s.source_contract_id && (parseFloat(s.rolled_amount) || 0) > 0)
    .map((s) => ({
      source_contract_id: parseInt(s.source_contract_id, 10),
      rolled_amount: round2((parseFloat(s.rolled_amount) || 0) * invFactor),
    }));
};

// Map contract data to form values for edit mode
const mapContractToFormValues = (contract) => {
  if (!contract) return getDefaultValues();

  // T127 (ADDENDUM 2): capital is persisted in the CONTRACT currency (canonical). When the
  // contract is "received in the settlement currency", the wizard form holds capture-currency
  // (settlement) values and converts on save. So on edit-load we REVERSE the conversion
  // (contract → settlement = ÷ rate) for capital/new/rolled, keeping the save-time conversion
  // symmetric and avoiding a double-conversion on the next save. (Per-investor rows are loaded
  // from the API separately and reverse-converted in the same way where they are fetched.)
  const fxRate = parseFloat(contract.settlement_fx_rate);
  const reverseConvert = contract.settlement_currency_id
    && contract.capital_received_in_settlement_ccy
    && Number.isFinite(fxRate) && fxRate > 0;
  const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
  const toCapture = (n) => {
    if (n == null || n === '') return n === 0 ? 0 : '';
    return reverseConvert ? round2((parseFloat(n) || 0) / fxRate) : n;
  };

  return {
    is_multi_investor: contract.is_multi_investor || false,
    investor_id: contract.investor_id || '',
    product_id: contract.product_id || '',
    capital_amount: toCapture(contract.capital_amount),
    new_funds_amount: toCapture(contract.new_funds_amount),
    rolled_funds_amount: reverseConvert ? round2((parseFloat(contract.rolled_funds_amount) || 0) / fxRate) : (contract.rolled_funds_amount || 0),
    user_reference: contract.user_reference || '',
    rolled_from_contract_id: contract.rolled_from_contract_id || null,
    rolled_from_reference: contract.rolled_from_reference || '',
    rolled_from_user_reference: contract.rolled_from_user_reference || '',
    // T3-032: seed the rollover-source rows from the legacy single link so the section
    // shows the existing source immediately; the real multi-source list is fetched
    // asynchronously after load and overwrites this (loadRolloverSources).
    rollover_sources: contract.rolled_from_contract_id && (parseFloat(contract.rolled_funds_amount) || 0) > 0
      ? [{
          source_contract_id: contract.rolled_from_contract_id,
          rolled_amount: reverseConvert ? round2((parseFloat(contract.rolled_funds_amount) || 0) / fxRate) : (contract.rolled_funds_amount || 0),
          reference: contract.rolled_from_reference || null,
          user_reference: contract.rolled_from_user_reference || null,
        }]
      : [],
    investment_currency_id: contract.investment_currency_id || contract.currency_id || '',
    settlement_currency_id: contract.settlement_currency_id || '',
    settlement_fx_rate: contract.settlement_fx_rate != null ? String(contract.settlement_fx_rate) : '',
    capital_received_in_settlement_ccy: contract.capital_received_in_settlement_ccy || false,
    investment_term: contract.investment_term || 'Years',
    investment_term_value: contract.investment_term_value || '',
    deposit_date: contract.deposit_date ? format(new Date(contract.deposit_date), 'yyyy-MM-dd') : '',
    maturity_date: contract.maturity_date ? format(new Date(contract.maturity_date), 'yyyy-MM-dd') : '',
    funds_received_date: contract.funds_received_date ? format(new Date(contract.funds_received_date), 'yyyy-MM-dd') : '',
    first_payment_date: contract.first_payment_date ? format(new Date(contract.first_payment_date), 'yyyy-MM-dd') : '',
    rate_type: contract.rate_type || 'Fixed',
    day_count_basis: contract.day_count_basis || '365',
    likelihood_to_roll: contract.likelihood_to_roll || '',
    interest_rate: contract.interest_rate || '',
    interest_paid_frequency_id: contract.interest_paid_frequency_id || '',
    capital_paid_frequency_id: contract.capital_paid_frequency_id || '',
    benchmark_rate_id: contract.benchmark_rate_id || null,
    rate_spread: contract.rate_spread || 0,
    rate_change_effective_basis: contract.rate_change_effective_basis || 'immediate',
    // T3-068: specified_date cadence — id as a string for the Select, date via format() so the native
    // date input binds (a raw ISO timestamp would not). Mirrors first_repricing_date handling below.
    rate_change_frequency_id: contract.rate_change_frequency_id || '',
    first_rate_change_date: contract.first_rate_change_date ? format(new Date(contract.first_rate_change_date), 'yyyy-MM-dd') : '',
    // T3-098: rehydrate the custom cadence so reopening a contract shows the toggle already on
    // (InterestPaymentsStep derives the gate from the unit — no separate boolean to sync).
    rate_change_custom_unit: contract.rate_change_custom_unit || '',
    rate_change_custom_value: contract.rate_change_custom_value ?? '',
    rate_change_mode: contract.rate_change_mode || 'bulk',
    exclude_leap_day: contract.exclude_leap_day || false,
    eligible_for_repricing: contract.eligible_for_repricing || false,
    repricing_frequency_id: contract.repricing_frequency_id || '',
    first_repricing_date: contract.first_repricing_date ? format(new Date(contract.first_repricing_date), 'yyyy-MM-dd') : '',
    eligible_for_renewal: contract.eligible_for_renewal || false,
    renewal_frequency_id: contract.renewal_frequency_id || '',
    first_renewal_date: contract.first_renewal_date ? format(new Date(contract.first_renewal_date), 'yyyy-MM-dd') : '',
    allow_schedule_editing: contract.allow_schedule_editing || false,
    is_brokered: contract.is_brokered || false,
    broker_id: contract.broker_id || '',
    broker_fees: contract.broker_fees || '',
    // T3-012: broker-fee VAT snapshot read back from the contract on edit.
    broker_fee_vat_percent: contract.broker_fee_vat_percent ?? '',
    broker_fee_exclusive_amount: contract.broker_fee_exclusive_amount ?? '',
    broker_fee_vat_amount: contract.broker_fee_vat_amount ?? '',
    broker_fee_inclusive_amount: contract.broker_fee_inclusive_amount ?? '',
    // T3-084: ?? 'STANDARD' is what makes a pre-T3-084 contract open on Standard-Rate.
    broker_fee_vat_treatment: contract.broker_fee_vat_treatment ?? 'STANDARD',
    broker_fee_settled_in_other_ccy: contract.broker_fee_settled_in_other_ccy === true,
    broker_fee_settlement_currency_id: contract.broker_fee_settlement_currency_id ?? '',
    broker_fee_vat_note: contract.broker_fee_vat_note ?? '',
    placement_fees_id: contract.placement_fees_id || '',
    issuer_id: contract.issuer_id || '',
    program_id: contract.program_id || '',
    stamp_duty: contract.stamp_duty || false,
    stamp_duty_fees: contract.stamp_duty_fees || '',
    stamp_duty_fees_id: contract.stamp_duty_fees_id || '',
    has_collateral: contract.has_collateral || false,
    collateral_provider_id: contract.collateral_provider_id || '',
    collateral_type_id: contract.collateral_type_id || '',
    collateral_amount: contract.collateral_amount || '',
    collateral_multiplier: contract.collateral_multiplier || '',
    collateral_in_favour_of: contract.collateral_in_favour_of || '',
    collateral_description: contract.collateral_description || '',
    calculation_profile_id: contract.calculation_profile_id || '',
    capitalization_frequency_id: contract.capitalization_frequency_id || '',
    equal_installment: contract.equal_installment ?? false,
    combine_payment_rows: contract.combine_payment_rows ?? false,
  };
};

// Inner component that has access to context
function ContractWizardInner({ initialStepOverride = null, scheduleJustGenerated = false }) {
  const navigate = useNavigate();
  const notify = useNotification();
  const { hasPermission, canApprove } = usePermissions();
  const canAdmin = hasPermission(MODULES.CONTRACT_MANAGEMENT, PERMISSION_LEVELS.ADMIN);
  const canActivate = canApprove(MODULES.CONTRACT_MANAGEMENT);
  const { downloadPDF, isGenerating: isGeneratingPDF } = useServerPDF();
  const [pendingRateChange, setPendingRateChange] = useState(null);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [redemptionDialogOpen, setRedemptionDialogOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitDialogContractId, setSubmitDialogContractId] = useState(null);
  const [createdContractId, setCreatedContractId] = useState(null);
  const autoSaveInFlight = React.useRef(false);
  const saveInFlight = React.useRef(false);
  // Tracks whether loadContractInvestors has successfully populated the
  // originalContractInvestors snapshot for an edit-mode contract. Distinguishes
  // "not loaded yet / load failed" from "genuinely no investors" so the save path
  // never diffs against a stale empty snapshot and re-adds already-allocated
  // investors (which the insert-only SP rejects with "already allocated").
  const investorsLoaded = React.useRef(false);

  const {
    isEdit,
    contractId,
    initialData,
    refDataLoading,
    refDataError,
    contractInvestors,
    setContractInvestors,
    originalContractInvestors,
    setOriginalContractInvestors,
    contractCollaterals,
    setContractCollaterals,
    originalContractCollaterals,
    setOriginalContractCollaterals,
    contractStampDuties,
    setContractStampDuties,
    originalContractStampDuties,
    setOriginalContractStampDuties,
    adhocCapitalEvents,
    setAdhocCapitalEvents,
    originalAdhocCapitalEvents,
    setOriginalAdhocCapitalEvents,
    adhocInterestPayments,
    setAdhocInterestPayments,
    originalAdhocInterestPayments,
    setOriginalAdhocInterestPayments,
    hasSchedule,
    setHasSchedule,
    setSchedulePreview,
    generatingSchedule,
    setGeneratingSchedule,
    submitting,
    setSubmitting,
    clearValidation,
    setValidationErrors,
    setValidationWarnings,
    completedSteps,
    markStepCompleted,
    showSummaryStep,
    setRequireLikelihoodToRoll,
    setEnableBrokerFeeScheduling,
  } = useContractFormContext();

  // Determine initial step
  // Priority: 1) initialStepOverride (from navigation state), 2) Draft → step 0, 3) showSummaryStep → step 5, 4) default to 0
  const getInitialStep = () => {
    if (initialStepOverride !== null && initialStepOverride !== undefined) {
      return initialStepOverride;
    }
    // Draft contracts open at step 0 (editable wizard), non-Draft open at summary
    if (isEdit && initialData?.status === 'Draft') return 0;
    if (showSummaryStep) return 5; // Contract Summary step
    return 0; // Start at Contract Details
  };

  const [activeStep, setActiveStep] = useState(getInitialStep);

  // After create→edit redirect with scheduleJustGenerated flag, fire the success toast once on mount.
  useEffect(() => {
    if (scheduleJustGenerated) {
      notify.success('Amortization schedule has been generated. You can now submit for approval.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on mount
  }, []);

  // Load tenant workflow settings (Likelihood to Roll requirement) once on
  // mount. Failure defaults to false so the wizard is never blocked by a
  // settings fetch error.
  useEffect(() => {
    apiGet('/contracts/settings/workflow')
      .then(res => {
        setRequireLikelihoodToRoll(res?.data?.requireLikelihoodToRoll === true);
        // Feature 30: gates the whole fee-scheduling block in the broker dialog.
        setEnableBrokerFeeScheduling(res?.data?.enableBrokerFeeScheduling === true);
      })
      .catch(() => {
        setRequireLikelihoodToRoll(false);
        setEnableBrokerFeeScheduling(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on mount
  }, []);

  // Get the appropriate steps array
  const steps = getSteps(showSummaryStep);

  // Load reference data
  useContractReferenceData();

  // Form setup with react-hook-form
  const methods = useForm({
    defaultValues: isEdit ? mapContractToFormValues(initialData) : getDefaultValues(),
    mode: 'onChange',
  });

  const { handleSubmit, watch, reset, getValues } = methods;

  // T127 (ADDENDUM 2): the settlement→contract conversion factor for per-investor capital
  // rows, derived from the current form state. Returns 1 (no conversion) unless the contract
  // settles in a different currency AND the initial funds arrive in that settlement currency.
  const investorFxFactor = () => {
    const v = getValues();
    const rate = parseFloat(v.settlement_fx_rate);
    return (v.settlement_currency_id && v.capital_received_in_settlement_ccy === true && Number.isFinite(rate) && rate > 0)
      ? rate
      : 1;
  };

  // Validation hook
  const { runStepValidation, runFullValidation, validateStep } = useContractValidation();

  // Get the effective contract ID — from props (edit mode) or from a previous auto-save (create mode)
  const getEffectiveContractId = () => contractId || createdContractId;

  // Load adhoc events and investors for edit mode
  useEffect(() => {
    if (isEdit && contractId) {
      loadContractInvestors();
      loadContractCollaterals();
      loadContractStampDuties();
      loadContractBrokerFee();
      loadAdhocCapitalEvents();
      loadAdhocInterestPayments();
      loadRolloverSources();
      checkScheduleExists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load functions are defined below and run once when edit mode opens
  }, [isEdit, contractId]);

  // Load pending rate change if contract has amendment status
  useEffect(() => {
    if (isEdit && contractId && initialData?.amendment_status === 'Pending Rate Change') {
      apiGet(`/rate-changes/contract/${contractId}`)
        .then(res => setPendingRateChange(res?.data?.pending || null))
        .catch(err => logger.error('Error loading pending rate change', { message: err.message }));
    }
  }, [isEdit, contractId, initialData?.amendment_status]);

  // One-time initial form hydration in edit mode. We deliberately do NOT
  // re-reset on every initialData change: react-hook-form's reset() is
  // destructive — it discards any in-progress user edits the form holds.
  // This effect previously fired on every initialData change, which caused
  // a class of bugs where a user's edit (e.g. correcting funds_received_date)
  // got clobbered by the stale stored value when initialData was refreshed
  // (or simply by the second effect-run under React Strict Mode). The
  // initial-load case is still handled here, and any subsequent fresh-data
  // event (e.g. after a save round-trip) should patch specific fields
  // explicitly rather than blowing away the whole form.
  const initialResetDone = useRef(false);
  useEffect(() => {
    if (initialResetDone.current) return;
    if (isEdit && initialData) {
      reset(mapContractToFormValues(initialData));
      initialResetDone.current = true;
    }
  }, [isEdit, initialData, reset]);

  // Format ISO date strings (from API) to yyyy-MM-dd for HTML date inputs
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    try { return format(new Date(dateStr), 'yyyy-MM-dd'); }
    catch { return dateStr; }
  };

  const loadContractInvestors = async () => {
    try {
      // Refresh WHT snapshot from investor master before fetching, so master
      // updates in Stakeholder Management show up. Server enforces the same
      // status gate; this check just avoids a needless write on active contracts.
      if (initialData?.status === 'Draft' || initialData?.status === 'Pending Approval') {
        try {
          await apiPost(`/contracts/${contractId}/investors/refresh-wht`);
        } catch (refreshErr) {
          logger.warn('WHT refresh failed', { message: refreshErr.message });
        }
      }
      const response = await apiGet(`/contracts/${contractId}/investors`);
      const investorsArray = response?.data || response || [];
      // T127 (ADDENDUM 2): per-investor capital is persisted contract-ccy. When the contract is
      // "received in the settlement currency", reverse-convert to capture (settlement) ccy on
      // load so the form holds capture values and the save-time conversion stays symmetric.
      const fxRate = parseFloat(initialData?.settlement_fx_rate);
      const reverse = initialData?.settlement_currency_id
        && initialData?.capital_received_in_settlement_ccy
        && Number.isFinite(fxRate) && fxRate > 0;
      const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
      const mapped = reverse
        ? investorsArray.map((inv) => {
            const cap = round2((parseFloat(inv.capital_amount) || 0) / fxRate);
            const rolled = round2((parseFloat(inv.rolled_funds_amount) || 0) / fxRate);
            return { ...inv, capital_amount: cap, new_funds_amount: round2(cap - rolled), rolled_funds_amount: rolled };
          })
        : investorsArray;
      setContractInvestors(mapped);
      setOriginalContractInvestors(mapped);
      investorsLoaded.current = true;
    } catch (err) {
      // Do NOT swallow into a "no investors" state — leaving the snapshot empty
      // would make saveContractInvestors treat every allocation as new and re-POST
      // it. Keep investorsLoaded false so the save path knows to reload first.
      investorsLoaded.current = false;
      logger.error('Error loading contract investors', { message: err.message });
    }
  };

  const loadContractCollaterals = async () => {
    try {
      const response = await apiGet(`/contracts/${contractId}/collaterals`);
      const rows = response?.data || response || [];
      setContractCollaterals(rows);
      setOriginalContractCollaterals(rows);
    } catch (err) {
      logger.error('Error loading contract collaterals', { message: err.message });
    }
  };

  const loadContractStampDuties = async () => {
    try {
      const response = await apiGet(`/contracts/${contractId}/stamp-duties`);
      const rows = response?.data || response || [];
      setContractStampDuties(rows);
      setOriginalContractStampDuties(rows);
    } catch (err) {
      logger.error('Error loading contract stamp duties', { message: err.message });
    }
  };

  // Seed the fee CADENCE from the broker-fee junction row (Feature 30).
  //
  // ⚠ THIS CANNOT COME FROM `initialData`, AND THAT IS THE WHOLE REASON IT EXISTS.
  // fn_contract_get_by_id returns NONE of the cadence fields (fee_frequency_id, next_fee_date,
  // paid_in_advance are all absent), and the `repricing_frequency_id` it DOES return is the
  // contract-level interest-RATE repricing cadence on contract_master — a different column on a
  // different table that happens to share a name. Seeding from the contract object would show
  // the rate cadence on the broker fee screen and save it back as the fee cadence.
  //
  // Returns a single object (not an array), so there is no snapshot to diff against.
  // The remaining broker fields (broker_id, broker_fees, the VAT snapshot) already arrive via
  // mapContractToFormValues from the contract_master scalars, so only the cadence is seeded here.
  const loadContractBrokerFee = async () => {
    try {
      const response = await apiGet(`/broker-fees/contract/${contractId}`);
      const fee = response?.data ?? null;
      if (!fee) return; // not brokered, or a pre-Feature-30 contract with no junction row

      // ⚠ The namespaced keys are mandatory — see saveContractBrokerFee. Writing a bare
      // `repricing_frequency_id` here would overwrite the user's interest-rate cadence.
      // `methods.setValue` rather than a destructured `setValue`: this component destructures
      // only handleSubmit/watch/reset/getValues from useForm, and widening that line would touch
      // shared state for a fee-only change.
      methods.setValue('broker_fee_frequency_id', fee.fee_frequency_id ?? '');
      methods.setValue('broker_fee_next_date', formatDateForInput(fee.next_fee_date));
      methods.setValue('broker_fee_paid_in_advance', fee.paid_in_advance !== false);
      methods.setValue('broker_fee_repricing_frequency_id', fee.repricing_frequency_id ?? '');
      methods.setValue('broker_fee_next_repricing_date', formatDateForInput(fee.next_repricing_date));
    } catch (err) {
      logger.error('Error loading contract broker fee', { message: err.message });
    }
  };

  // T3-032: load the many-to-1 rollover sources for edit-mode rehydrate. Overwrites the
  // single-row fallback seeded in mapContractToFormValues. Amounts are stored in the
  // contract currency; reverse-convert to the capture currency when the contract took its
  // initial capital in the settlement currency (mirrors rolled_funds_amount handling).
  const loadRolloverSources = async () => {
    try {
      const response = await apiGet(`/contracts/${contractId}/rollover-sources`);
      const rows = response?.data || response || [];
      if (!Array.isArray(rows) || rows.length === 0) return; // keep the seed / empty
      const c = initialData || {};
      const fxRate = parseFloat(c.settlement_fx_rate);
      const reverseConvert = !!c.settlement_currency_id && c.capital_received_in_settlement_ccy === true
        && Number.isFinite(fxRate) && fxRate > 0;
      const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
      methods.setValue('rollover_sources', rows.map((r) => ({
        source_contract_id: r.source_contract_id,
        rolled_amount: reverseConvert ? round2((parseFloat(r.rolled_amount) || 0) / fxRate) : (parseFloat(r.rolled_amount) || 0),
        reference: r.reference || null,
        user_reference: r.user_reference || null,
      })));
    } catch (err) {
      logger.error('Error loading rollover sources', { message: err.message });
    }
  };

  const loadAdhocCapitalEvents = async () => {
    try {
      const response = await apiGet(`/contracts/${contractId}/adhoc-capital`);
      const events = response?.data || response || [];
      const mapped = events.map(e => ({ ...e, event_date: formatDateForInput(e.event_date), isNew: false }));
      setAdhocCapitalEvents(mapped);
      setOriginalAdhocCapitalEvents(mapped);
    } catch (err) {
      logger.error('Error loading adhoc capital events', { message: err.message });
    }
  };

  const loadAdhocInterestPayments = async () => {
    try {
      const response = await apiGet(`/contracts/${contractId}/adhoc-interest-payments`);
      const events = response?.data || response || [];
      const mapped = events.map(e => ({ ...e, event_date: formatDateForInput(e.event_date), isNew: false }));
      setAdhocInterestPayments(mapped);
      setOriginalAdhocInterestPayments(mapped);
    } catch (err) {
      logger.error('Error loading adhoc interest payments', { message: err.message });
    }
  };

  const checkScheduleExists = async () => {
    try {
      const response = await apiGet(`/amortization/contract/${contractId}`);
      const scheduleData = response?.data || response;
      setHasSchedule(scheduleData?.schedule?.length > 0);
    } catch (err) {
      logger.warn('Error checking schedule existence, defaulting to regenerate mode', { message: err?.message });
      setHasSchedule(true);
    }
  };

  // Render a list of validation errors as a single toast message
  const fireValidationToast = (errors) => {
    if (!errors || errors.length === 0) return;
    notify.error(
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {errors.map((err, i) => <li key={i}>{err}</li>)}
      </Box>
    );
  };

  // Navigation handlers

  // Validate the current step and persist it (steps 0–3) BEFORE leaving it.
  // Returns true if it is safe to leave the step, false if we must stay
  // (client-side invalid, or the server rejected the save). Shared by handleNext
  // AND handleStepClick so a stepper jump behaves identically to Next — the same
  // validation, the same blocking save, the same server-reason toast — rather than
  // silently changing step without persisting the current edits.
  const commitCurrentStep = async () => {
    const formValues = watch();
    const result = runStepValidation(activeStep, formValues);

    if (!result.valid) {
      fireValidationToast(result.errors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }

    clearValidation();
    // The step passed client-side validation — clear any stale toast raised by an
    // earlier failed Next/Save so it doesn't linger after the user fixed it.
    notify.dismiss();

    const stepToSave = activeStep;

    // Save BEFORE leaving so a server-side rejection (e.g. the rolled-funds cap,
    // which the client cannot always evaluate) is caught at THIS step rather than
    // slipping through to Generate Schedule. Step 4 (Review) has its own save flow.
    if (stepToSave <= 3) {
      // Flag submitting so WizardNavigation disables its buttons — and the stepper
      // is disabled too (isLoading = submitting || generatingSchedule) — during the
      // round-trip; prevents a double-click firing a second save or navigating on a
      // phantom in-flight save.
      setSubmitting(true);
      let saveResult;
      try {
        saveResult = await autoSaveDraft(stepToSave);
      } finally {
        setSubmitting(false);
      }
      if (!saveResult.ok) {
        // Stay on the current step and tell the user exactly why the save was
        // refused. Data stays in the form; the user fixes it and tries again.
        fireValidationToast([saveResult.message, 'Your data is still in the form — adjust it and continue.']);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return false;
      }
    }

    // Save succeeded (or nothing to save) — safe to leave the step.
    markStepCompleted(stepToSave);
    return true;
  };

  const handleNext = async () => {
    if (!(await commitCurrentStep())) return;
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  // Live validity of the current step, recomputed each render against the
  // watched form state. validateStep is a pure function (no state writes),
  // so this is safe to call inline; runStepValidation cannot be used here
  // because it sets validation-error state and would cause an infinite render
  // loop. Used to drive the Next button's disabled state — the user gets
  // immediate visual feedback as they fill in required fields rather than
  // having to click Next to learn the step is incomplete.
  // watch() (no args) subscribes this component to every form-state change.
  const watchedFormValues = watch();
  const isCurrentStepValid = (() => {
    try {
      const result = validateStep(activeStep, watchedFormValues);
      return !!result?.valid;
    } catch {
      return false;
    }
  })();

  const handleBack = () => {
    clearValidation();
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  // Handle clicking on a step in the stepper.
  // Mirrors handleNext: validate + persist the CURRENT step (via commitCurrentStep)
  // before jumping, so edits made on the step being left are saved as part of the
  // navigation — exactly like clicking Next — instead of silently changing step with
  // the changes left unpersisted. A server-side rejection keeps the user on the step
  // with the real reason.
  const handleStepClick = async (stepIndex) => {
    // Can only click on completed steps (unchanged) — and never onto the current one.
    if (!completedSteps.includes(stepIndex) || stepIndex === activeStep) return;
    // Ignore clicks while a save/schedule round-trip is already running so we don't
    // fire a redundant commit or navigate on a phantom in-flight save. (WizardStepper
    // is also visually disabled during this window.)
    if (submitting || generatingSchedule || autoSaveInFlight.current) return;
    if (!(await commitCurrentStep())) return; // invalid or rejected — stay put, reason shown
    setActiveStep(stepIndex);
  };

  const handleCancel = () => {
    navigate('/contracts');
  };

  const handleDownloadPDF = async () => {
    if (!contractId) return;
    try {
      const filename = `Contract_${initialData?.reference || contractId}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      await downloadPDF({ type: 'contract', id: contractId, filename });
    } catch (err) {
      logger.error('Error downloading contract PDF', { message: err.message });
      notify.error('Failed to generate contract PDF: ' + (err.message || 'Please try again.'));
    }
  };

  const handleActivate = async () => {
    try {
      await apiPost(`/contracts/${contractId}/activate`);
      window.location.reload();
    } catch (err) {
      logger.error('Error activating contract', { message: err.message, status: err.response?.status });
      notify.error('Failed to activate contract: ' + extractApiError(err));
    }
  };

  // Build payload from form data
  const buildPayload = (data, status = 'Draft') => {
    // T127 (ADDENDUM 2): when the contract settles in a different currency AND the initial
    // funds arrive in that settlement currency, the capital inputs (capital/new/rolled, and
    // per-investor amounts) are captured in the SETTLEMENT currency. The contract is canonical
    // in the CONTRACT currency, so convert here on save: contract_amount = entered × rate
    // (settlement_fx_rate is settlement→contract). We round each component to 2dp and derive
    // new = capital − rolled so chk_capital_source_sum (capital = new + rolled) stays exact.
    const fxRate = parseFloat(data.settlement_fx_rate);
    const convertCapital = !!data.settlement_currency_id
      && data.capital_received_in_settlement_ccy === true
      && Number.isFinite(fxRate) && fxRate > 0;
    const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
    const toContract = (n) => convertCapital ? round2((parseFloat(n) || 0) * fxRate) : (n != null && n !== '' ? parseFloat(n) : null);

    // Component conversion (rolled first, then derive new from the converted total so the sum holds).
    let convertedCapital = data.capital_amount != null && data.capital_amount !== '' ? toContract(data.capital_amount) : null;
    const convertedRolled = convertCapital ? round2((parseFloat(data.rolled_funds_amount) || 0) * fxRate) : (data.rolled_funds_amount != null && data.rolled_funds_amount !== '' ? parseFloat(data.rolled_funds_amount) : null);
    const convertedNew = convertCapital
      ? round2((convertedCapital || 0) - (convertedRolled || 0))
      : (data.new_funds_amount != null && data.new_funds_amount !== '' ? parseFloat(data.new_funds_amount) : null);

    // Defence-in-depth: for a single-investor contract, capital must equal new + rolled.
    // A contract that was just switched multi->single could otherwise carry a stale
    // composite capital synced from the removed allocations; force the sum so a regen
    // never runs against an inconsistent capital_amount. (Multi-investor capital is
    // synced from the allocation rows, so leave it untouched there.)
    if (!data.is_multi_investor) {
      const sum = round2((convertedNew || 0) + (convertedRolled || 0));
      if (sum > 0) convertedCapital = sum;
    }

    return {
    ...data,
    capital_amount: convertedCapital,
    new_funds_amount: convertedNew,
    rolled_funds_amount: convertedRolled,
    interest_rate: data.interest_rate ? parseFloat(data.interest_rate) : null,
    investment_term_value: data.investment_term_value ? parseFloat(data.investment_term_value) : null,
    day_count_basis: data.day_count_basis,
    // Coerce empty dates to null so a cleared field never reaches the API as ''.
    // (deposit_date is required so it should never be empty on a valid save, but
    // this keeps the payload null-clean and consistent with the other dates.)
    deposit_date: data.deposit_date || null,
    maturity_date: data.maturity_date || null,
    funds_received_date: data.funds_received_date || null,
    first_payment_date: data.first_payment_date || null,
    broker_fees: data.broker_fees ? parseFloat(data.broker_fees) : null,
    // T3-012: broker-fee VAT snapshot (vat % from the contract country; the three
    // amounts derived in BrokerSection). Cleared when not brokered; the SP also
    // forces these to NULL when there is no broker.
    broker_fee_vat_percent: data.is_brokered && data.broker_fee_vat_percent !== '' && data.broker_fee_vat_percent != null ? parseFloat(data.broker_fee_vat_percent) : null,
    broker_fee_exclusive_amount: data.is_brokered && data.broker_fee_exclusive_amount !== '' && data.broker_fee_exclusive_amount != null ? parseFloat(data.broker_fee_exclusive_amount) : null,
    broker_fee_vat_amount: data.is_brokered && data.broker_fee_vat_amount !== '' && data.broker_fee_vat_amount != null ? parseFloat(data.broker_fee_vat_amount) : null,
    broker_fee_inclusive_amount: data.is_brokered && data.broker_fee_inclusive_amount !== '' && data.broker_fee_inclusive_amount != null ? parseFloat(data.broker_fee_inclusive_amount) : null,
    // T3-084: VAT treatment + the cross-border facts. Gated on is_brokered like the snapshot
    // above, and the companions additionally gated on NO_VAT so Standard never ships them
    // (the SP clears them anyway, and chk_broker_fee_vat_standard_no_settlement enforces it).
    broker_fee_vat_treatment: data.is_brokered ? (data.broker_fee_vat_treatment || 'STANDARD') : 'STANDARD',
    broker_fee_settled_in_other_ccy: data.is_brokered && data.broker_fee_vat_treatment === 'NO_VAT'
      ? data.broker_fee_settled_in_other_ccy === true : false,
    // The currency is owned by the settled-in-other-currency flag: no flag, no currency.
    broker_fee_settlement_currency_id: data.is_brokered && data.broker_fee_vat_treatment === 'NO_VAT'
      && data.broker_fee_settled_in_other_ccy === true
      && data.broker_fee_settlement_currency_id !== '' && data.broker_fee_settlement_currency_id != null
      ? parseInt(data.broker_fee_settlement_currency_id, 10) : null,
    broker_fee_vat_note: data.is_brokered && data.broker_fee_vat_treatment === 'NO_VAT'
      && data.broker_fee_vat_note ? (String(data.broker_fee_vat_note).trim() || null) : null,
    // T116: broker fee is wired to a 'Broker Fee' placement-fee reference row;
    // placement_fees_id drives the commission journal GL accounts. Cleared when
    // the contract is not brokered.
    placement_fees_id: data.is_brokered && data.placement_fees_id ? parseInt(data.placement_fees_id, 10) : null,
    // T113: stamp duty is managed via the contract_stamp_duties junction
    // (saveContractStampDuties), which resyncs the legacy contract_master
    // stamp_duty* columns server-side. The contract create/update payload no
    // longer carries stamp-duty fields directly.
    collateral_amount: data.collateral_amount ? parseFloat(data.collateral_amount) : null,
    collateral_multiplier: data.collateral_multiplier ? parseFloat(data.collateral_multiplier) : null,
    // Benchmark + spread apply to benchmark-linked types: 'Variable' (shown as
    // "Linked") AND 'Floating'. Both persist them; only 'Fixed' clears them.
    benchmark_rate_id: (data.rate_type === 'Variable' || data.rate_type === 'Floating') ? (data.benchmark_rate_id || null) : null,
    rate_spread: (data.rate_type === 'Variable' || data.rate_type === 'Floating') ? parseFloat(data.rate_spread || 0) : 0,
    // Effective-basis + update-mode stay Variable-only (the ad-hoc rate-change workflow).
    rate_change_effective_basis: data.rate_type === 'Variable' ? (data.rate_change_effective_basis || 'immediate') : 'immediate',
    // T3-068: specified_date cadence — only sent for that basis on a Variable contract; nulled
    // otherwise (the SP/DB also clear them, but keep the payload honest, like renewal below).
    rate_change_frequency_id: data.rate_type === 'Variable' && data.rate_change_effective_basis === 'specified_date' && data.rate_change_frequency_id
      ? parseInt(data.rate_change_frequency_id, 10)
      : null,
    first_rate_change_date: data.rate_type === 'Variable' && data.rate_change_effective_basis === 'specified_date'
      ? (data.first_rate_change_date || null)
      : null,
    // T3-098: custom cadence — sent as a PAIR or not at all. Both null for any other basis/rate
    // type, which makes the API clear a previously-saved cadence (sp_contract_set_rate_change_cadence
    // treats a NULL in either slot as "clear"), so switching the toggle or the basis off cannot
    // leave a stale interval driving the rate timeline.
    rate_change_custom_unit: data.rate_type === 'Variable'
      && data.rate_change_effective_basis === 'specified_date'
      && (data.rate_change_custom_unit === 'months' || data.rate_change_custom_unit === 'years')
      && parseInt(data.rate_change_custom_value, 10) > 0
      ? data.rate_change_custom_unit
      : null,
    rate_change_custom_value: data.rate_type === 'Variable'
      && data.rate_change_effective_basis === 'specified_date'
      && (data.rate_change_custom_unit === 'months' || data.rate_change_custom_unit === 'years')
      && parseInt(data.rate_change_custom_value, 10) > 0
      ? parseInt(data.rate_change_custom_value, 10)
      : null,
    rate_change_mode: data.rate_type === 'Variable' ? (data.rate_change_mode || 'bulk') : 'bulk',
    exclude_leap_day: data.exclude_leap_day || false,
    eligible_for_repricing: data.eligible_for_repricing === true,
    repricing_frequency_id: data.eligible_for_repricing === true && data.repricing_frequency_id
      ? parseInt(data.repricing_frequency_id, 10)
      : null,
    // T127: renewal opt-out point (applies to all rate types). Frequency/date cleared off-flag.
    eligible_for_renewal: data.eligible_for_renewal === true,
    renewal_frequency_id: data.eligible_for_renewal === true && data.renewal_frequency_id
      ? parseInt(data.renewal_frequency_id, 10)
      : null,
    first_renewal_date: data.eligible_for_renewal === true ? (data.first_renewal_date || null) : null,
    // T105: Floating-only manual schedule-editing gate. Backend rejects true on
    // non-Floating contracts; force false off-Floating to stay consistent.
    allow_schedule_editing: data.rate_type === 'Floating' && data.allow_schedule_editing === true,
    program_id: data.program_id ? parseInt(data.program_id, 10) : null,
    // T117: settlement currency (null => settles in contract currency). The SP normalises
    // to null when it equals the contract currency.
    settlement_currency_id: data.settlement_currency_id ? parseInt(data.settlement_currency_id, 10) : null,
    // T119: initial-capital FX rate (only meaningful when multi-currency; SP clears it otherwise).
    settlement_fx_rate: data.settlement_currency_id && data.settlement_fx_rate ? parseFloat(data.settlement_fx_rate) : null,
    // T127: receipt-direction flag — only meaningful when multi-currency; force false otherwise.
    capital_received_in_settlement_ccy: !!data.settlement_currency_id && data.capital_received_in_settlement_ccy === true,
    status,
    is_multi_investor: data.is_multi_investor,
    investor_id: data.is_multi_investor ? null : data.investor_id,
    likelihood_to_roll: data.is_multi_investor ? null : (data.likelihood_to_roll || null),
    // T3-032: many-to-1 rolled funds (single-investor path). Emit the per-source breakdown
    // [{source_contract_id, rolled_amount}] (settlement-ccy converted like the scalar), and
    // mirror the first source onto rolled_from_contract_id. The API writes the junction via
    // sp_contract_set_rollover_sources; the SP checks SUM(rolled_amount) = rolled_funds_amount.
    ...(function () {
      if (data.is_multi_investor) return { rollover_sources: [] };
      const src = Array.isArray(data.rollover_sources) ? data.rollover_sources : [];
      const conv = src
        .filter((s) => s.source_contract_id && (parseFloat(s.rolled_amount) || 0) > 0)
        .map((s) => ({
          source_contract_id: parseInt(s.source_contract_id, 10),
          rolled_amount: convertCapital ? round2((parseFloat(s.rolled_amount) || 0) * fxRate) : round2(s.rolled_amount),
        }));
      return {
        rollover_sources: conv,
        rolled_from_contract_id: conv[0]?.source_contract_id || null,
      };
    })(),
    };
  };

  // T127 (ADDENDUM 2): convert a per-investor capital row settlement→contract for the API,
  // mirroring buildPayload's conversion. Used when serialising multi-investor contracts so
  // contract_investors rows are stored in the contract (canonical) currency.
  const convertInvestorForApi = (inv, fxRate) => {
    // fxRate === 1 (or invalid) means single-currency / contract-ccy capture → no conversion.
    if (!Number.isFinite(fxRate) || fxRate <= 0 || fxRate === 1) return inv;
    const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
    const cap = round2((parseFloat(inv.capital_amount) || 0) * fxRate);
    const rolled = round2((parseFloat(inv.rolled_funds_amount) || 0) * fxRate);
    const fresh = round2(cap - rolled);
    return { ...inv, capital_amount: cap, new_funds_amount: fresh, rolled_funds_amount: rolled };
  };

  // Save adhoc capital events
  const saveAdhocCapitalEvents = async (targetContractId) => {
    // Delete events that were removed from the form (existed in DB but no longer in current array)
    const currentCapitalIds = new Set(adhocCapitalEvents.filter(e => e.id).map(e => e.id));
    const removedCapitalEvents = originalAdhocCapitalEvents.filter(e => e.id && !currentCapitalIds.has(e.id));
    for (const event of removedCapitalEvents) {
      await apiDelete(`/contracts/${targetContractId}/adhoc-capital/${event.id}`);
    }

    // Create or update remaining events
    for (const event of adhocCapitalEvents) {
      if (!event.event_date || !event.amount) continue;

      if (event.isNew || !event.id) {
        await apiPost(`/contracts/${targetContractId}/adhoc-capital`, {
          event_date: event.event_date,
          event_type: event.event_type,
          amount: parseFloat(event.amount),
          description: event.description || null,
        });
      } else {
        await apiPut(`/contracts/${targetContractId}/adhoc-capital/${event.id}`, {
          event_date: event.event_date,
          event_type: event.event_type,
          amount: parseFloat(event.amount),
          description: event.description || null,
        });
      }
    }
    // Reload from API to get real DB IDs and update originals snapshot
    try {
      const response = await apiGet(`/contracts/${targetContractId}/adhoc-capital`);
      const events = response?.data || response || [];
      const mapped = events.map(e => ({ ...e, event_date: formatDateForInput(e.event_date), isNew: false }));
      setAdhocCapitalEvents(mapped);
      setOriginalAdhocCapitalEvents(mapped);
    } catch (err) {
      logger.error('Error reloading adhoc capital events', { message: err.message });
    }
  };

  // Save adhoc interest payment events
  const saveAdhocInterestPayments = async (targetContractId) => {
    // Delete events that were removed from the form
    const currentInterestIds = new Set(adhocInterestPayments.filter(e => e.id).map(e => e.id));
    const removedInterestEvents = originalAdhocInterestPayments.filter(e => e.id && !currentInterestIds.has(e.id));
    for (const event of removedInterestEvents) {
      await apiDelete(`/contracts/${targetContractId}/adhoc-interest-payments/${event.id}`);
    }

    // Create or update remaining events
    for (const event of adhocInterestPayments) {
      if (!event.event_date) continue;

      if (event.isNew || !event.id) {
        await apiPost(`/contracts/${targetContractId}/adhoc-interest-payments`, {
          event_date: event.event_date,
          description: event.description || null,
          amount: event.amount != null && event.amount !== '' ? parseFloat(event.amount) : null,
        });
      } else {
        await apiPut(`/contracts/${targetContractId}/adhoc-interest-payments/${event.id}`, {
          event_date: event.event_date,
          description: event.description || null,
          amount: event.amount != null && event.amount !== '' ? parseFloat(event.amount) : null,
        });
      }
    }
    // Reload from API to get real DB IDs and update originals snapshot
    try {
      const response = await apiGet(`/contracts/${targetContractId}/adhoc-interest-payments`);
      const events = response?.data || response || [];
      const mapped = events.map(e => ({ ...e, event_date: formatDateForInput(e.event_date), isNew: false }));
      setAdhocInterestPayments(mapped);
      setOriginalAdhocInterestPayments(mapped);
    } catch (err) {
      logger.error('Error reloading adhoc interest payments', { message: err.message });
    }
  };

  // Save contract investors (edit mode — diff-based: delete removed, create new, update changed)
  const saveContractInvestors = async (targetContractId) => {
    // If we're editing a contract whose existing investors were never successfully
    // loaded (initial GET failed, or the save fired before the async load resolved),
    // originalContractInvestors is still its initial []. Diffing against that would
    // classify already-allocated investors as "new" and re-POST them → the insert-only
    // sp_contract_add_investor raises "Investor is already allocated to this contract".
    // Reconcile against the server before diffing. Best-effort: if the reload fails we
    // still proceed, but the POST-then-PUT fallback below keeps the save correct.
    let baselineInvestors = originalContractInvestors;
    if (isEdit && !investorsLoaded.current) {
      try {
        const existing = await apiGet(`/contracts/${targetContractId}/investors`);
        baselineInvestors = existing?.data || existing || [];
        investorsLoaded.current = true;
      } catch (err) {
        logger.warn('Could not reconcile existing investors before save; relying on add/update fallback', { message: err.message });
      }
    }

    const currentInvestorIds = new Set(contractInvestors.map(i => i.investor_id));
    const originalInvestorIds = new Set(baselineInvestors.map(i => i.investor_id));

    // Did the investor structure change (add / remove / edited amount or source)?
    // Any such change makes an existing amortization schedule stale — the schedule
    // is generated per-investor from these allocations, so we must invalidate it
    // (see below) rather than leave orphaned/mismatched rows.
    const origById = new Map(baselineInvestors.map(i => [i.investor_id, i]));
    const structureChanged =
      contractInvestors.length !== baselineInvestors.length ||
      contractInvestors.some((cur) => {
        const orig = origById.get(cur.investor_id);
        if (!orig) return true; // added
        return (
          parseFloat(orig.capital_amount || 0) !== parseFloat(cur.capital_amount || 0) ||
          parseFloat(orig.new_funds_amount || 0) !== parseFloat(cur.new_funds_amount || 0) ||
          parseFloat(orig.rolled_funds_amount || 0) !== parseFloat(cur.rolled_funds_amount || 0) ||
          (orig.rolled_from_contract_id || null) !== (cur.rolled_from_contract_id || null)
        );
      });

    // Delete removed investors
    const removedInvestors = baselineInvestors.filter(i => !currentInvestorIds.has(i.investor_id));
    for (const investor of removedInvestors) {
      await apiDelete(`/contracts/${targetContractId}/investors/${investor.investor_id}`);
    }

    // Create new investors or update existing ones
    const invFactor = investorFxFactor();
    for (const investor of contractInvestors) {
      const conv = convertInvestorForApi(investor, invFactor);
      const investorPayload = {
        investor_id: conv.investor_id,
        capital_amount: parseFloat(conv.capital_amount),
        new_funds_amount: parseFloat(conv.new_funds_amount) || 0,
        rolled_funds_amount: parseFloat(conv.rolled_funds_amount) || 0,
        likelihood_to_roll: conv.likelihood_to_roll || null,
        // Per-investor prior contract — only meaningful when this investor rolls
        // funds; the SP forces NULL otherwise. Persisted on both create and update.
        rolled_from_contract_id: (parseFloat(conv.rolled_funds_amount) || 0) > 0
          ? (conv.rolled_from_contract_id || null)
          : null,
        // Send the FULL source breakdown alongside the scalar, so the receiver can tell a
        // MULTI-source roll from a single-source one. rolled_from_contract_id above mirrors only
        // sources[0], so on a 2-source roll the scalar total (e.g. 100k) and that one source (50k
        // pool) describe DIFFERENT things — and the pre-flight/guard, seeing no sign a second
        // source exists, compared the whole total against the first source's pool and 400'd
        // ("Rolled funds … exceed the remaining unallocated balance of prior contract X"). That
        // aborted the request before the authoritative per-source setter below could run, so the
        // breakdown was persisted nowhere. The array is informational for validation only — the
        // rollover-sources PUT further down remains the sole WRITER of the junction.
        rollover_sources: sourcesForApi(investor, invFactor),
      };

      if (!originalInvestorIds.has(investor.investor_id)) {
        // New investor. If the baseline snapshot was stale (e.g. an id type/shape
        // mismatch, or a reload we couldn't fully reconcile), the row may already
        // exist server-side. The add SP is insert-only and raises a 400 "already
        // allocated" in that case — fall back to an update rather than aborting the
        // whole save (which previously blocked schedule generation). Only recover
        // from that specific condition; any other error still propagates.
        try {
          await apiPost(`/contracts/${targetContractId}/investors`, investorPayload);
        } catch (err) {
          const status = err?.response?.status;
          const message = extractApiError(err, '');
          // Recover from "this investor already exists server-side" however the API phrases it:
          // the SP's own guard raises 'Investor is already allocated' (400), but a duplicate could
          // also surface as a 409 DuplicateError from a raw unique-constraint violation. Previously
          // only the 400 + /already allocated/ form was caught, so a 409 aborted the whole save —
          // that is what made "Duplicate value for contract" unrecoverable when re-adding a
          // previously removed investor (now fixed at source by the revive in
          // sp_contract_add_investor; this stays as defence-in-depth). Any other error still
          // propagates — never swallow a failure we don't understand.
          if ((status === 400 || status === 409) && /already allocated|duplicate/i.test(message)) {
            logger.warn('Investor already allocated on add; updating instead', { investor: investor.investor_id, status });
            await apiPut(`/contracts/${targetContractId}/investors/${investor.investor_id}`, investorPayload);
          } else {
            throw err;
          }
        }
      } else {
        // Existing investor — update
        await apiPut(`/contracts/${targetContractId}/investors/${investor.investor_id}`, investorPayload);
      }

      // T3-034: many-to-1 rolled funds per investor. When this investor rolls from more than
      // one prior contract (or we want the junction populated for a single source), write the
      // per-investor rollover-source breakdown AFTER the investor row (the SP's per-investor
      // sum check reads the just-written rolled_funds_amount). Sources convert to settlement
      // currency like the scalar. Only write when this investor rolls funds and its sources
      // reconcile to the rolled amount (the modal gates this; be tolerant of legacy rows with
      // no explicit sources by falling back to the single rolled_from_contract_id).
      const invRolled = parseFloat(conv.rolled_funds_amount) || 0;
      if (invRolled > 0) {
        const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
        // Same derivation the investor payload above sent for validation (sourcesForApi), so the
        // amounts validated and the amounts written are identical by construction.
        let sources = sourcesForApi(investor, invFactor);
        // Fallback: a single source captured only as rolled_from_contract_id (legacy / one-source
        // investor) — represent it as one junction row so the junction stays authoritative.
        if (sources.length === 0 && investorPayload.rolled_from_contract_id) {
          sources = [{ source_contract_id: investorPayload.rolled_from_contract_id, rolled_amount: investorPayload.rolled_funds_amount }];
        }
        // Only PUT when the sources reconcile to the investor's rolled amount (± rounding);
        // otherwise leave the junction as-is (the DB SP would hard-fail on a mismatch, and an
        // in-progress edit shouldn't break the save — mirrors the single-investor tolerant sync).
        const sum = round2(sources.reduce((a, s) => a + (parseFloat(s.rolled_amount) || 0), 0));
        if (sources.length > 0 && Math.abs(sum - parseFloat(investorPayload.rolled_funds_amount)) <= 0.01) {
          // T3-057: do NOT swallow a failed rollover-sources PUT. Silently warning here reported a
          // successful save while leaving the investor's roll without its junction breakdown — the
          // junction-authoritative reports then showed the roll wrong (Rolled 0 / orange). The DB
          // now writes a single-source mirror on the per-investor scalar write (sp_contract_add/
          // update_investor), so the roll is not LOST if this refine-to-precise-breakdown PUT
          // fails — but a real failure must surface, not hide behind a false "saved" toast.
          await apiPut(`/contracts/${targetContractId}/investors/${investor.investor_id}/rollover-sources`, { sources });
        }
      }
    }

    // Reload from API to sync state
    try {
      const response = await apiGet(`/contracts/${targetContractId}/investors`);
      const investorsArray = response?.data || response || [];
      setContractInvestors(investorsArray);
      setOriginalContractInvestors(investorsArray);
      investorsLoaded.current = true;
    } catch (err) {
      logger.error('Error reloading contract investors', { message: err.message });
    }

    // Invalidate a stale schedule: if the investor structure changed and a schedule
    // already exists, the old (per-investor) rows no longer match the contract. Clear
    // them and drop hasSchedule so the wizard shows "Generate Schedule" and blocks
    // submit-for-approval until the user regenerates. Callers that regenerate right
    // after (handleGenerateSchedule) simply rebuild it; save-only paths leave it
    // cleared. Best-effort — never fail the save on cleanup.
    if (structureChanged && hasSchedule) {
      try {
        await apiDelete(`/amortization/contract/${targetContractId}`);
        setHasSchedule(false);
        setSchedulePreview(null);
      } catch (err) {
        logger.warn('Failed to invalidate stale schedule after investor change', { message: err.message });
      }
    }
  };

  // Save contract collaterals (edit + create modes — diff-based against the
  // originally-loaded snapshot: delete removed rows, create new ones, update
  // changed ones). Rows that did not exist server-side have no `id`.
  const saveContractCollaterals = async (targetContractId) => {
    const currentIds = new Set(contractCollaterals.filter(c => c.id !== undefined).map(c => c.id));
    const originalIds = (originalContractCollaterals || []).map(c => c.id).filter(id => id !== undefined);

    // Delete rows that existed before and are gone now
    for (const oldId of originalIds) {
      if (!currentIds.has(oldId)) {
        await apiDelete(`/contracts/${targetContractId}/collaterals/${oldId}`);
      }
    }

    for (const row of contractCollaterals) {
      const payload = {
        collateral_provider_id: row.collateral_provider_id,
        secured: row.secured !== false,
        currency_id: row.currency_id ?? null,
        collateral_amount: row.collateral_amount,
        collateral_type_id: row.collateral_type_id ?? null,
        collateral_multiplier: row.collateral_multiplier ?? null,
        nominal_amount: row.nominal_amount ?? null,
        collateral_in_favour_of: row.collateral_in_favour_of ?? null,
        collateral_description: row.collateral_description ?? null,
        contingent_liability: row.contingent_liability ?? null,
      };
      if (row.id === undefined) {
        await apiPost(`/contracts/${targetContractId}/collaterals`, payload);
      } else {
        await apiPut(`/contracts/${targetContractId}/collaterals/${row.id}`, payload);
      }
    }

    // Reload to capture server-generated ids for new rows
    try {
      const response = await apiGet(`/contracts/${targetContractId}/collaterals`);
      const rows = response?.data || response || [];
      setContractCollaterals(rows);
      setOriginalContractCollaterals(rows);
    } catch (err) {
      logger.error('Error reloading contract collaterals', { message: err.message });
    }
  };

  // Save contract stamp duties (T113 — diff-based against the originally-loaded
  // snapshot, same pattern as collaterals). Each row references a 'Stamp Duty'
  // placement-fee row; rows without an `id` are new.
  const saveContractStampDuties = async (targetContractId) => {
    const currentIds = new Set((contractStampDuties || []).filter(s => s.id !== undefined).map(s => s.id));
    const originalIds = (originalContractStampDuties || []).map(s => s.id).filter(id => id !== undefined);

    // Delete rows that existed before and are gone now
    for (const oldId of originalIds) {
      if (!currentIds.has(oldId)) {
        await apiDelete(`/contracts/${targetContractId}/stamp-duties/${oldId}`);
      }
    }

    for (const row of (contractStampDuties || [])) {
      // T114: include the (possibly overridden) per-contract percentage.
      const payload = {
        stamp_duty_fees_id: row.stamp_duty_fees_id,
        percentage: row.percentage != null && row.percentage !== '' ? parseFloat(row.percentage) : null,
      };
      if (row.id === undefined) {
        await apiPost(`/contracts/${targetContractId}/stamp-duties`, payload);
      } else {
        // PUT when the referenced row OR the overridden percentage changed.
        const original = (originalContractStampDuties || []).find(o => o.id === row.id);
        const refChanged = !original || original.stamp_duty_fees_id !== row.stamp_duty_fees_id;
        const pctChanged = !original || parseFloat(original.percentage) !== parseFloat(row.percentage);
        if (refChanged || pctChanged) {
          await apiPut(`/contracts/${targetContractId}/stamp-duties/${row.id}`, payload);
        }
      }
    }

    // Reload to capture server-generated ids for new rows
    try {
      const response = await apiGet(`/contracts/${targetContractId}/stamp-duties`);
      const rows = response?.data || response || [];
      setContractStampDuties(rows);
      setOriginalContractStampDuties(rows);
    } catch (err) {
      logger.error('Error reloading contract stamp duties', { message: err.message });
    }
  };

  // Save the broker fee junction row (Feature 30 / T3-132). The legacy broker scalars
  // still travel on the contract payload via buildPayload — this writes the JUNCTION row
  // that the fee engine reads, which buildPayload cannot reach.
  //
  // ⚠ WITHOUT THIS, A CAPTURED FEE SPLITS THE RECORD RATHER THAN FAILING.
  // Measured on dev contract 53 (MTN-SZ-2026-0002, Active, approved): contract_master held
  // is_brokered=true / broker_id=1 / broker_fees=1.0000, while contract_broker_fees held
  // ZERO rows — so the contract read as brokered with no fee record and no schedule, and no
  // error was raised at any point. The scalars persisted because buildPayload sends them; the
  // junction did not because nothing called this.
  //
  // ⚠ THE NON-BROKERED EARLY RETURN IS LOAD-BEARING — do NOT copy the stamp-duty shape here.
  // PUT /api/broker-fees/contract/:id throws 400 'A broker is required to record a broker fee'
  // when broker_id is absent (routes/broker-fees.js:121), every call site awaits this, and a
  // throw aborts the whole enclosing save. Without the guard EVERY non-brokered contract would
  // fail its own save. saveContractStampDuties has no equivalent hazard, which is exactly why
  // the pattern must not be mirrored blindly.
  //
  // Idempotent upsert on a single row, so — unlike stamp duty — there is no diff against an
  // `original…` snapshot and no per-row id to recapture.
  const saveContractBrokerFee = async (targetContractId) => {
    const formData = watch();
    if (!formData.broker_id) return; // not brokered — nothing to save

    const nullIfBlank = (v) => (v === '' || v === undefined ? null : v);
    const intOrNull = (v) => (v === '' || v == null ? null : parseInt(v, 10));
    const floatOrNull = (v) => (v === '' || v == null ? null : parseFloat(v));

    // ⚠ TWO NAME TRANSLATIONS HAPPEN HERE, both deliberate:
    //   1. `broker_fees` (form) -> `fee_percent` (API/junction column).
    //   2. The five cadence keys are namespaced `broker_fee_*` in the form but UNPREFIXED in
    //      the API body. The namespacing exists because `repricing_frequency_id` is ALSO a
    //      contract-level form key owned by InterestPaymentsStep (the interest-RATE repricing
    //      cadence, sent to contract_master by buildPayload). All wizard steps share ONE
    //      FormProvider, so an unprefixed fee key would silently overwrite the user's rate
    //      cadence. Never un-namespace these.
    const payload = {
      broker_id: intOrNull(formData.broker_id),
      placement_fees_id: intOrNull(formData.placement_fees_id),
      fee_percent: floatOrNull(formData.broker_fees),
      // The VAT snapshot, passed straight through. The endpoint does NOT recompute from
      // capital — the struck-once rule (CLAUDE.md) means these are the figures agreed at
      // submission, and only an approved repricing may re-strike them.
      broker_fee_vat_percent: floatOrNull(formData.broker_fee_vat_percent),
      broker_fee_exclusive_amount: floatOrNull(formData.broker_fee_exclusive_amount),
      broker_fee_vat_amount: floatOrNull(formData.broker_fee_vat_amount),
      broker_fee_inclusive_amount: floatOrNull(formData.broker_fee_inclusive_amount),
      broker_fee_vat_treatment: formData.broker_fee_vat_treatment || 'STANDARD',
      broker_fee_settled_in_other_ccy: formData.broker_fee_settled_in_other_ccy === true,
      broker_fee_settlement_currency_id: intOrNull(formData.broker_fee_settlement_currency_id),
      broker_fee_vat_note: nullIfBlank(formData.broker_fee_vat_note),
      // The cadence.
      fee_frequency_id: intOrNull(formData.broker_fee_frequency_id),
      next_fee_date: nullIfBlank(formData.broker_fee_next_date),
      paid_in_advance: formData.broker_fee_paid_in_advance !== false,
      repricing_frequency_id: intOrNull(formData.broker_fee_repricing_frequency_id),
      next_repricing_date: nullIfBlank(formData.broker_fee_next_repricing_date),
      // The custom repricing interval is not captured by the dialog and the API enforces it
      // as a both-or-neither pair, so both go as null rather than one half of a pair.
      repricing_custom_unit: null,
      repricing_custom_value: null,
    };

    await apiPut(`/broker-fees/contract/${targetContractId}`, payload);
  };

  // Generate the broker fee schedule, swallowing every failure (Feature 30).
  //
  // ⚠ THE SWALLOW IS THE FEATURE, NOT LAZINESS. Both callers are paths whose PRIMARY job is
  // something else that must not fail because of a fee:
  //   - handleGenerateSchedule — generating the amortization schedule, which is the only thing
  //     that enables the Submit button (WizardNavigation: `hasSchedule` gates it).
  //   - handleSubmitForApproval — putting the contract forward for approval.
  // regenerateFeeSchedule throws on an already-repriced fee by design, so an un-caught call here
  // would block a user from generating a schedule or submitting a contract, which is a far worse
  // outcome than a fee ledger that is one save stale.
  //
  // ⚠ It logs but does NOT notify. A `skipped` result is the normal, expected case (fee
  // scheduling off for the tenant or no cadence on this contract), and a toast on every save
  // would train users to ignore toasts. A genuine refusal is rare and recoverable — the fee
  // schedule is regenerated on the next save.
  const generateFeeScheduleQuietly = async (targetContractId) => {
    if (!targetContractId) return;

    // ⚠ DON'T ASK WHEN THERE IS NOTHING TO GENERATE. Same guard, same reason, as
    // saveContractBrokerFee above: no broker means no fee, so the call is a guaranteed no-op.
    // Measured on dev: 62 of 70 contracts are not brokered, and each was firing this on EVERY save
    // and EVERY submit — previously logging a 500 server-side every time.
    //
    // ⚠ THIS IS THE NOISE FIX, NOT THE CORRECTNESS FIX. The route now returns `skipped` for a
    // contract with no fee, which is what actually makes the behaviour right. This guard is
    // deliberately NOT relied on alone: `is_brokered` / `broker_id` and the contract_broker_fees
    // row are kept in step BY CONVENTION, not by any FK or CHECK — and T3-139 exists precisely
    // because they once drifted apart (the split-record backfill, where the scalars said brokered
    // and the junction row was missing). If that happened again in the other direction, a UI-only
    // gate would silently stop generating a schedule for a genuinely brokered contract.
    if (!watch('broker_id')) return;

    try {
      const res = await apiPost(`/broker-fees/contract/${targetContractId}/schedule/generate`);
      const data = res?.data || res;
      if (data?.skipped) {
        logger.info('Fee schedule not generated', { contractId: targetContractId, reason: data.reason });
      }
    } catch (err) {
      // Never surfaced to the user, never rethrown — see the block comment above.
      logger.warn('Fee schedule generation failed (non-fatal)', {
        contractId: targetContractId,
        message: extractApiError(err, 'unknown'),
      });
    }
  };

  // Auto-save draft on step transition (called by handleNext BEFORE advancing).
  // Does NOT navigate away. Does NOT validate (client-side validation already done
  // in handleNext). Returns a result the caller uses to gate navigation:
  //   { ok: true }            — save succeeded (or nothing to save); safe to advance
  //   { ok: false, message }  — the server rejected the save; STAY on the step and
  //                             show `message` (the server's real reason, e.g. the
  //                             rolled-funds cap). This is what surfaces a server-only
  //                             business rule at the step it originates instead of
  //                             letting it slip through to Generate Schedule.
  const autoSaveDraft = async (fromStep) => {
    if (autoSaveInFlight.current || saveInFlight.current) return { ok: true };
    autoSaveInFlight.current = true;
    saveInFlight.current = true;
    try {
      const formData = watch();
      const payload = buildPayload(formData, 'Draft');
      const effectiveId = getEffectiveContractId();

      if (effectiveId) {
        await apiPut(`/contracts/${effectiveId}`, payload);
      } else {
        // Create mode, first save
        const result = await apiPost('/contracts', payload);
        const newId = result?.data?.id || result?.id;
        if (newId) {
          setCreatedContractId(newId);
          if (formData.is_multi_investor && contractInvestors.length > 0) {
            await saveContractInvestors(newId);
          }
          if (formData.has_collateral && contractCollaterals.length > 0) {
            await saveContractCollaterals(newId);
          }
          await saveContractStampDuties(newId);
          await saveContractBrokerFee(newId);
          return { ok: true };
        } else {
          // The POST returned 2xx but no id — treat as a soft failure so the user
          // is told to save explicitly rather than advancing on a phantom save.
          return { ok: false, message: 'Contract was saved but the reference could not be captured. Use "Save & Close" to save explicitly.' };
        }
      }

      // Save related entities based on which step we're leaving
      const targetId = effectiveId || createdContractId;
      if (!targetId) return { ok: true };

      // Investors are captured on Step 0. Sync on leaving it when multi is on,
      // OR when it was just toggled off but server-side rows still exist (the
      // diff wipes them → backend flips is_multi_investor=false).
      if (fromStep === 0 && (formData.is_multi_investor || (originalContractInvestors || []).length > 0)) {
        await saveContractInvestors(targetId);
      }
      // Collateral is captured on the Fees & Security step; sync it whenever
      // the user advances past that step (or any later one) so partial work
      // is not lost between auto-saves.
      if (formData.has_collateral && fromStep >= 3) {
        await saveContractCollaterals(targetId);
      }
      // Stamp duties live on the same Fees & Security step (3); sync once past it.
      if (fromStep >= 3) {
        await saveContractStampDuties(targetId);
        // The broker fee is captured on that same step, so it syncs on the same gate.
        await saveContractBrokerFee(targetId);
      }
      if (fromStep === 2) {
        await saveAdhocCapitalEvents(targetId);
        await saveAdhocInterestPayments(targetId);
      }
      return { ok: true };
    } catch (err) {
      // Read the server's REAL message (extractApiError checks data.error.message
      // first — the shape the API actually uses); the old `data.message` read was
      // always undefined, so users saw a generic fallback instead of the reason.
      const message = extractApiError(err, 'Auto-save failed');
      logger.warn('Auto-save failed', { message });
      return { ok: false, message };
    } finally {
      autoSaveInFlight.current = false;
      saveInFlight.current = false;
    }
  };

  // Save & close
  const handleSaveDraft = async (data) => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    try {
      setSubmitting(true);
      clearValidation();

      // Save & Close is a DRAFT-STASH action: persist what's captured, NOT the full
      // current step. Gate only on the minimum-viable-Draft floor — product, currency,
      // capital (>0), both dates, term, and (single-investor) the investor — which the
      // server hard-requires even for a Draft (the PUT path has no inline 400 gate, so
      // an empty payload would surface as a raw DB error rather than a friendly toast).
      // That floor is exactly Step-0's validation, so reuse it regardless of which step
      // the user is on; everything else (interest rate, calc profile, frequency, …) is
      // auto-defaulted server-side and may be left blank. Submit for Approval remains
      // the real completeness gate.
      const floorResult = runStepValidation(0, data);
      if (!floorResult.valid) {
        // Surface WHY the save was refused (a floor field is missing). Previously this
        // ran the full current-step validation and bailed on any incomplete step.
        fireValidationToast(floorResult.errors);
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const payload = buildPayload(data, 'Draft');
      const effectiveId = getEffectiveContractId();

      if (effectiveId) {
        // Update existing contract (edit mode OR create mode after auto-save created it)
        await apiPut(`/contracts/${effectiveId}`, payload);
        // Sync investors when multi-investor is on, OR when it was just toggled
        // off but server-side rows still exist — the diff wipes them, and the
        // last delete flips is_multi_investor=false in the DB.
        if (data.is_multi_investor || (originalContractInvestors || []).length > 0) {
          await saveContractInvestors(effectiveId);
        }
        if (data.has_collateral) {
          await saveContractCollaterals(effectiveId);
        } else if ((originalContractCollaterals || []).length > 0) {
          // User toggled has_collateral off — wipe any server-side rows.
          await saveContractCollaterals(effectiveId);
        }
        await saveContractStampDuties(effectiveId);
        await saveContractBrokerFee(effectiveId);
        await saveAdhocCapitalEvents(effectiveId);
        await saveAdhocInterestPayments(effectiveId);
        // Save & Close always exits the wizard back to the contracts list,
        // regardless of edit vs create-after-autosave. Previously edit mode
        // routed to the contract summary which surprised users — the button
        // is "Save & Close", not "Save & View".
        navigate('/contracts');
      } else {
        // Create new contract (no prior auto-save)
        const result = await apiPost('/contracts', payload);
        const newContractId = result?.data?.id || result?.id;

        // Add investors for multi-investor contracts
        if (data.is_multi_investor && newContractId && contractInvestors.length > 0) {
          const invFactor = investorFxFactor();
          for (const investor of contractInvestors) {
            const conv = convertInvestorForApi(investor, invFactor);
            await apiPost(`/contracts/${newContractId}/investors`, {
              investor_id: conv.investor_id,
              capital_amount: parseFloat(conv.capital_amount),
              new_funds_amount: parseFloat(conv.new_funds_amount) || 0,
              rolled_funds_amount: parseFloat(conv.rolled_funds_amount) || 0,
              likelihood_to_roll: conv.likelihood_to_roll || null,
              rolled_from_contract_id: (parseFloat(conv.rolled_funds_amount) || 0) > 0
                ? (conv.rolled_from_contract_id || null)
                : null,
            });
          }
        }

        // Add collaterals
        if (data.has_collateral && newContractId && contractCollaterals.length > 0) {
          await saveContractCollaterals(newContractId);
        }
        if (newContractId) {
          await saveContractStampDuties(newContractId);
          await saveContractBrokerFee(newContractId);
        }

        // Add adhoc events
        await saveAdhocCapitalEvents(newContractId);
        await saveAdhocInterestPayments(newContractId);

        navigate('/contracts');
      }
    } catch (err) {
      logger.error('Error saving contract', { message: err.message, status: err.response?.status });
      notify.error(extractApiError(err, 'Failed to save contract'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
      saveInFlight.current = false;
    }
  };

  // Submit for approval — persists pending changes then opens the document
  // upload dialog. The dialog itself fires POST /contracts/:id/submit-for-approval
  // with the chosen document_id (hard-block: cannot submit without a document).
  const handleSubmitForApproval = async (data) => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    try {
      setSubmitting(true);
      clearValidation();

      // Full validation before submission
      const validationResult = runFullValidation(data);
      if (!validationResult.valid) {
        fireValidationToast(validationResult.errors);
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // Build payload with Draft status — the submit-for-approval call flips to
      // 'Pending Approval' once a document is attached via the dialog.
      const payload = buildPayload(data, 'Draft');
      let targetId = getEffectiveContractId();

      if (targetId) {
        await apiPut(`/contracts/${targetId}`, payload);
        if (data.is_multi_investor || (originalContractInvestors || []).length > 0) {
          await saveContractInvestors(targetId);
        }
        if (data.has_collateral || (originalContractCollaterals || []).length > 0) {
          await saveContractCollaterals(targetId);
        }
        await saveContractStampDuties(targetId);
        await saveContractBrokerFee(targetId);
        await saveAdhocCapitalEvents(targetId);
        await saveAdhocInterestPayments(targetId);

        // Feature 30 — THE BACKSTOP. The user generates the fee schedule at the Review step, but
        // may then edit the fee percentage or cadence and come straight here. Without this, the
        // persisted fee ledger would silently disagree with the fee being submitted.
        //
        // ⚠ Regenerated UNCONDITIONALLY rather than behind a staleness flag, because the engine
        // is idempotent — verified on dev contract 376 in a rolled-back transaction: three
        // successive regenerations left 25 rows, 1005.00/1005.00 and version 1 unchanged. A flag
        // comparing six fee fields would be more code AND a new way to be wrong (a missed field
        // silently skips the refresh), for no benefit over simply re-running it.
        //
        // ⚠ It runs BEFORE the dialog opens, and therefore before
        // POST /contracts/:id/submit-for-approval fires from SubmitForApprovalDialog. That
        // ordering is deliberate: the submit route runs OUTSIDE any transaction, so a fee write
        // added there could not roll back with the status flip and could strand a contract in
        // Pending Approval with a half-written ledger. Here it reuses the fee route's own
        // wrapInTransaction instead.
        await generateFeeScheduleQuietly(targetId);
      } else {
        // Create then continue with the new id (shouldn't happen - submit disabled in create mode)
        const result = await apiPost('/contracts', payload);
        targetId = result?.data?.id || result?.id;
        if (!targetId) {
          throw new Error('Failed to create contract — no id returned');
        }
      }

      setSubmitDialogContractId(targetId);
      setSubmitDialogOpen(true);
    } catch (err) {
      logger.error('Error preparing contract for submission', { message: err.message, status: err.response?.status });
      notify.error(extractApiError(err, 'Failed to save contract before submission'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
      saveInFlight.current = false;
    }
  };

  const handleSubmissionDialogClose = () => {
    setSubmitDialogOpen(false);
    setSubmitDialogContractId(null);
  };

  const handleSubmissionComplete = () => {
    setSubmitDialogOpen(false);
    const id = submitDialogContractId;
    setSubmitDialogContractId(null);
    navigate(isEdit && id ? `/contracts/${id}` : '/contracts');
  };

  // Generate/regenerate amortization schedule
  const handleGenerateSchedule = async () => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    try {
      setGeneratingSchedule(true);

      const formData = watch();

      // Pre-flight: validate every prior step before generating a schedule.
      // Otherwise broken inputs (e.g. interest_rate = 0) silently produce a real
      // schedule and the error surfaces only at submit time.
      const allErrors = [];
      const allWarnings = [];
      for (let s = 0; s <= 3; s++) {
        const r = validateStep(s, formData);
        allErrors.push(...r.errors);
        allWarnings.push(...r.warnings);
      }
      if (allErrors.length > 0) {
        setValidationErrors(allErrors);
        setValidationWarnings(allWarnings);
        fireValidationToast(allErrors);
        setGeneratingSchedule(false);
        saveInFlight.current = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const payload = buildPayload(formData, 'Draft');

      let targetContractId = getEffectiveContractId();

      if (targetContractId) {
        // Edit mode or create mode after auto-save: update existing contract
        const contractStatus = initialData?.status;
        const isEditable = !contractStatus || contractStatus === 'Draft';
        if (isEditable) {
          await apiPut(`/contracts/${targetContractId}`, payload);
        }
        if (formData.is_multi_investor || (originalContractInvestors || []).length > 0) {
          await saveContractInvestors(targetContractId);
        }
        if (formData.has_collateral || (originalContractCollaterals || []).length > 0) {
          await saveContractCollaterals(targetContractId);
        }
        await saveContractStampDuties(targetContractId);
        // ⚠ DELIBERATE DIVERGENCE FROM THE JUNCTION PATTERN — the fee is Draft-gated here while
        // investors, collateral and stamp duty above are not. A fee CADENCE drives an accrual
        // schedule and payment events, so re-upserting it on an Active contract is materially
        // different from re-saving a stamp-duty rate. Reuses the `isEditable` flag already
        // computed above for the contract PUT. Do not "align" this with its neighbours.
        if (isEditable) {
          await saveContractBrokerFee(targetContractId);
        }
        await saveAdhocCapitalEvents(targetContractId);
        await saveAdhocInterestPayments(targetContractId);
      } else {
        // Create mode: create the contract first
        const result = await apiPost('/contracts', payload);
        targetContractId = result?.data?.id || result?.id;

        if (!targetContractId) {
          throw new Error('Failed to create contract - no ID returned');
        }

        setCreatedContractId(targetContractId);

        // Add investors for multi-investor contracts
        if (formData.is_multi_investor && contractInvestors.length > 0) {
          const invFactor = investorFxFactor();
          for (const investor of contractInvestors) {
            const conv = convertInvestorForApi(investor, invFactor);
            await apiPost(`/contracts/${targetContractId}/investors`, {
              investor_id: conv.investor_id,
              capital_amount: parseFloat(conv.capital_amount),
              new_funds_amount: parseFloat(conv.new_funds_amount) || 0,
              rolled_funds_amount: parseFloat(conv.rolled_funds_amount) || 0,
              likelihood_to_roll: conv.likelihood_to_roll || null,
              rolled_from_contract_id: (parseFloat(conv.rolled_funds_amount) || 0) > 0
                ? (conv.rolled_from_contract_id || null)
                : null,
            });
          }
        }

        // Add collaterals
        if (formData.has_collateral && contractCollaterals.length > 0) {
          await saveContractCollaterals(targetContractId);
        }
        await saveContractStampDuties(targetContractId);
        await saveContractBrokerFee(targetContractId);

        // Add adhoc events
        await saveAdhocCapitalEvents(targetContractId);
        await saveAdhocInterestPayments(targetContractId);
      }

      // Generate the schedule
      const response = hasSchedule
        ? await apiPost(`/amortization/regenerate/${targetContractId}`)
        : await apiPost(`/amortization/generate/${targetContractId}`);

      const scheduleData = response?.data || response;
      if (scheduleData?.schedule?.length > 0) {
        setSchedulePreview(scheduleData);
        setHasSchedule(true);
      } else {
        // Schedule generated but no data returned - fetch it
        const fetchResponse = await apiGet(`/amortization/contract/${targetContractId}`);
        const fetchedData = fetchResponse?.data || fetchResponse;
        if (fetchedData?.schedule?.length > 0) {
          setSchedulePreview(fetchedData);
        }
        setHasSchedule(true);
      }

      // Feature 30: generate the BROKER FEE schedule in the same explicit action, so the fee
      // ledger and the amortization schedule are always struck from the same saved state.
      //
      // ⚠ DELIBERATELY NON-FATAL, AND THAT IS THE WHOLE POINT OF THE try/catch.
      // regenerateFeeSchedule THROWS on a fee that has already been repriced (a whole-term regen
      // would overlay the surviving V1 rows and double every total). This button is the ONLY gate
      // on submission — WizardNavigation disables "Submit for Approval" until hasSchedule is true
      // — so letting a fee-side refusal reject the amortization schedule would strand the user
      // with no way to submit at all. The amortization result above stands regardless.
      //
      // A `skipped` response is the NORMAL case, not an error: it means fee scheduling is off,
      // either for the tenant (enable_broker_fee_scheduling) or for this contract (no cadence).
      // Nothing is shown for it — an informational toast on every save would be noise.
      await generateFeeScheduleQuietly(targetContractId);

      // If this was a create, redirect to edit mode for the new contract
      // Use state to indicate we should stay on the Review & Schedule step (index 4)
      // and to show the success toast after the remount.
      if (!isEdit && targetContractId) {
        navigate(`/contracts/${targetContractId}/edit`, { state: { initialStep: 4, scheduleJustGenerated: true } });
      } else {
        notify.success('Amortization schedule has been generated. You can now submit for approval.');
      }
    } catch (err) {
      logger.error('Error generating schedule', { message: err.message, status: err.response?.status });
      notify.error(extractApiError(err, 'Failed to generate amortization schedule'));
    } finally {
      setGeneratingSchedule(false);
      saveInFlight.current = false;
    }
  };

  // Handle navigating to edit mode from summary step
  const handleEditContract = () => {
    clearValidation();
    setActiveStep(0); // Go to first step to edit
  };

  // Render step content
  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <ContractDetailsStep />;
      case 1:
        return <InterestPaymentsStep />;
      case 2:
        return <CapitalEventsFeesStep />;
      case 3:
        return <FeesSecurityStep />;
      case 4:
        return (
          <ReviewScheduleStep
            onGenerateSchedule={handleGenerateSchedule}
          />
        );
      case 5:
        return (
          <ContractSummaryStep
            onEditContract={handleEditContract}
          />
        );
      default:
        return null;
    }
  };

  // Loading state
  if (refDataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (refDataError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {refDataError}
        </Alert>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4">
              {isEdit ? (initialData?.user_reference || initialData?.reference || 'Contract Management') : 'Create New Contract'}
            </Typography>
            {isEdit && initialData && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                {initialData.user_reference && (
                  <Typography variant="body2" color="textSecondary">
                    {initialData.reference}
                  </Typography>
                )}
                <Chip label={initialData.status} size="small" color="default" />
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Pending Approval actions */}
            {isEdit && canAdmin && initialData?.status === 'Pending Approval' && (
              <>
                <Button variant="contained" color="success" startIcon={<ApproveIcon />} onClick={() => setApproveDialogOpen(true)}>
                  Approve
                </Button>
                <Button variant="outlined" color="error" startIcon={<RejectIcon />} onClick={() => setRejectDialogOpen(true)}>
                  Reject
                </Button>
              </>
            )}

            {/* Approved actions */}
            {isEdit && canActivate && initialData?.status === 'Approved' && (
              <Button variant="contained" color="primary" startIcon={<ApproveIcon />} onClick={handleActivate}>
                Activate Contract
              </Button>
            )}

            {/* Active actions */}
            {isEdit && canAdmin && initialData?.status === 'Active' && (
              <>
                {!initialData.amendment_status && (
                  <Button variant="contained" color="info" startIcon={<RedemptionIcon />} onClick={() => setRedemptionDialogOpen(true)}>
                    Capital Redemption
                  </Button>
                )}
                {/* Rollover retired 2026-07-29: the clone-based rollover (POST /contracts/:id/rollover
                    -> contract_rollover pending change -> sp_contract_rollover) is superseded by the
                    wizard's link-funds flow (create a new contract, link rolled funds from a FINALISED
                    source via sp_contract_set_rollover_sources). The button had been dead since T3-052
                    (2026-07-17) re-gated the route to Finalised-only while this button still rendered
                    for Active/Matured — every click 400'd. Route + SP are retained so the historical
                    executed rollovers stay auditable. */}
                <Button variant="outlined" color="error" startIcon={<TerminateIcon />} onClick={() => setTerminateDialogOpen(true)}>
                  Early Termination
                </Button>
              </>
            )}

            {/* Matured actions */}
            {isEdit && canAdmin && initialData?.status === 'Matured' && (
              <>
                {!initialData.amendment_status && (
                  <>
                    <Button variant="contained" color="primary" startIcon={<ExtendIcon />} onClick={() => setExtendDialogOpen(true)}>
                      Extend
                    </Button>
                    <Button variant="contained" color="info" startIcon={<RedemptionIcon />} onClick={() => setRedemptionDialogOpen(true)}>
                      Capital Redemption
                    </Button>
                  </>
                )}
                {/* Rollover retired 2026-07-29 — see the note on the Active-actions block above. */}
                <Button variant="outlined" color="error" startIcon={<TerminateIcon />} onClick={() => setTerminateDialogOpen(true)}>
                  Terminate
                </Button>
              </>
            )}

            {isEdit && (
              <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/contracts')}>
                Back to Contract List
              </Button>
            )}
          </Box>
        </Box>

        {/* Pending Rate Change Banner */}
        {isEdit && initialData?.amendment_status === 'Pending Rate Change' && pendingRateChange && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/approvals')}>
                View in Approvals
              </Button>
            }
          >
            <strong>Pending Rate Change:</strong> {pendingRateChange.old_rate}% → {pendingRateChange.new_rate}%
            {pendingRateChange.effective_date && ` (effective ${format(new Date(pendingRateChange.effective_date), 'dd MMM yyyy')})`}
          </Alert>
        )}

        {/* Matured Contract Action Required Banner */}
        {isEdit && initialData?.status === 'Matured' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Action Required:</strong>{' '}
            This contract matured on {initialData.maturity_date ? format(new Date(initialData.maturity_date), 'dd MMM yyyy') : 'N/A'}.
            Please <strong>Extend</strong> or <strong>Terminate</strong> this contract.
            {/* "Rollover" removed 2026-07-29 with the retired clone-based rollover button — to roll
                these funds, finalise the contract and link them from a new contract's Capital step. */}
            {' '}To roll these funds into a new contract, <strong>Finalise</strong> it first and then
            link the funds on the new contract.
          </Alert>
        )}

        {/* Pending Extension Banner */}
        {isEdit && initialData?.amendment_status === 'Pending Extension' && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/approvals')}>
                View in Approvals
              </Button>
            }
          >
            <strong>Pending Extension:</strong> This contract has a pending extension request awaiting approval.
          </Alert>
        )}

        {/* Stepper */}
        <WizardStepper
          activeStep={activeStep}
          completedSteps={completedSteps}
          onStepClick={handleStepClick}
          showSummaryStep={showSummaryStep}
          disabled={submitting || generatingSchedule}
        />

        {/* Navigation */}
        <WizardNavigation
          activeStep={activeStep}
          totalSteps={steps.length}
          showSummaryStep={showSummaryStep}
          nextDisabled={!isCurrentStepValid}
          onBack={handleBack}
          onNext={handleNext}
          // Save & Close is a DRAFT-STASH: call the handler directly with current
          // values instead of via handleSubmit, so RHF's field-level `required`
          // rules (on Step 0/1) don't swallow the click on a half-filled form.
          // handleSaveDraft itself gates on the minimum-viable-Draft floor.
          onSaveDraft={() => handleSaveDraft(getValues())}
          onSubmitForApproval={handleSubmit(handleSubmitForApproval)}
          onCancel={handleCancel}
          onEditContract={handleEditContract}
          onDownloadPDF={handleDownloadPDF}
          onGenerateSchedule={handleGenerateSchedule}
          isGeneratingPDF={isGeneratingPDF}
        />

        {/* Step Content */}
        {renderStep()}

        {/* Extend Contract Dialog */}
        {isEdit && initialData && (
          <ExtendContractDialog
            open={extendDialogOpen}
            onClose={() => setExtendDialogOpen(false)}
            contract={initialData}
            onSubmitSuccess={() => window.location.reload()}
          />
        )}

        {/* Capital Redemption Dialog */}
        {isEdit && initialData && (
          <AdhocCapitalAmendmentDialog
            open={redemptionDialogOpen}
            onClose={() => setRedemptionDialogOpen(false)}
            contract={initialData}
            onSubmitSuccess={() => window.location.reload()}
          />
        )}

        {/* Submit for Approval Dialog (mandatory signed-contract document) */}
        <SubmitForApprovalDialog
          open={submitDialogOpen}
          contractId={submitDialogContractId}
          onClose={handleSubmissionDialogClose}
          onSubmitted={handleSubmissionComplete}
        />

        {/* Action Dialogs (Approve, Reject, Terminate, Redemption) */}
        {isEdit && initialData && (
          <ContractActionDialogs
            contractId={contractId}
            contract={initialData}
            approveDialogOpen={approveDialogOpen}
            setApproveDialogOpen={setApproveDialogOpen}
            rejectDialogOpen={rejectDialogOpen}
            setRejectDialogOpen={setRejectDialogOpen}
            terminateDialogOpen={terminateDialogOpen}
            setTerminateDialogOpen={setTerminateDialogOpen}
            onActionComplete={() => window.location.reload()}
          />
        )}
      </Box>
    </FormProvider>
  );
}

// Main component with provider wrapper
function ContractWizard({ mode = 'create', contractId = null, initialData = null, initialStep = null, scheduleJustGenerated = false }) {
  return (
    <ContractFormProvider mode={mode} contractId={contractId} initialData={initialData}>
      <ContractWizardInner initialStepOverride={initialStep} scheduleJustGenerated={scheduleJustGenerated} />
    </ContractFormProvider>
  );
}

export default ContractWizard;
